import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import type {
  BackendDeleteResponse,
  BackendUpdateResponse,
  DatastoreDocument,
} from '@freelancer/datastore';
import { Datastore, OrderByDirection, uniqWith } from '@freelancer/datastore';
import type {
  Category,
  MembershipBenefitsCollection,
  Skill,
  SkillsCollection,
  UsersCollection,
  UserSkill,
  UserSkillsCollection,
} from '@freelancer/datastore/collections';
import { Feature, FeatureFlagsService } from '@freelancer/feature-flags';
import { TimeUtils } from '@freelancer/time-utils';
import { Tracking } from '@freelancer/tracking';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { StickyFooterPosition } from '@freelancer/ui/sticky-footer';
import { FontColor } from '@freelancer/ui/text';
import { isDefined, toNumber } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { Observable, Subscription } from 'rxjs';
import { BehaviorSubject, combineLatest, firstValueFrom, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  shareReplay,
  startWith,
} from 'rxjs/operators';
import { SkillsButtonType } from './skills.config';

@UntilDestroy({ className: 'SkillsComponent' })
@Component({
  selector: 'fl-skills',
  template: `
    <div
      class="SkillsContainer"
      flTrackingSection="{{ trackingSection }}"
    >
      <div class="SkillsSearchSection">
        <fl-banner-alert
          *ngIf="showSavingSkillError"
          i18n="Error saving skills message"
          [closeable]="false"
          [flMarginBottom]="'small'"
          [type]="BannerAlertType.ERROR"
        >
          There was an issue when saving your skills. Please try again. If the
          problem persists
          <fl-link
            link="/support"
            flTrackingLabel="ErrorSavingSupportLink"
          >
            contact support.
          </fl-link>
        </fl-banner-alert>

        <div [flMarginBottom]="'small'">
          <fl-search
            #search
            i18n-placeholder="Skill search placeholder"
            placeholder="Search a skill"
            [displayResults]="false"
            (query)="handleSearchText($event)"
          ></fl-search>
        </div>

        <fl-hr
          i18n-label="Skill selector divider text"
          label="OR"
          [flMarginBottomDesktop]="'small'"
          [flMarginBottomTablet]="'none'"
        ></fl-hr>
      </div>

      <div
        class="SkillsContent"
        [flMarginBottomDesktop]="'mid'"
      >
        <fl-skills-categories
          class="SkillsContent-card"
          [flHideMobile]="!(showCategories$ | flAsync)"
          [flMarginRightDesktop]="'mid'"
          [categories$]="categories$"
          (selectCategory)="handleSelectCategory($event); search.clearQuery()"
        ></fl-skills-categories>

        <fl-skills-category
          class="SkillsContent-card"
          [flHideMobile]="showCategories$ | flAsync"
          [flMarginRightDesktop]="'mid'"
          [filteredSkills]="filteredSkills$ | flAsync"
          [includeLocalInSkillsLimit]="
            (includeLocalInSkillsLimit$ | flAsync) ?? true
          "
          [loadingSkillSearch]="loadingSkillSearch$ | flAsync"
          [maxSkillsLimit]="maxSkillsLimit$ | flAsync"
          [searchText]="searchText$ | flAsync"
          [selectedCategory]="selectedCategory$ | flAsync"
          [selectedSkills]="selectedSkills$ | flAsync"
          (deselectSkill)="handleDeselectSkill($event)"
          (selectSkill)="handleSelectSkill($event)"
          (showCategories)="showCategories($event); search.clearQuery()"
          (showMaxSkillsLimit)="handleShowMaxSkillsLimit()"
        ></fl-skills-category>
        <fl-skills-selected
          class="SkillsContent-selected"
          [flShowDesktop]="true"
          [showErrorMessage]="
            showErrorMessage && (selectedSkills$ | flAsync)?.length === 0
          "
          [matchingSkillCount]="matchingSkillCount$ | flAsync"
          [maxSkillsLimit]="maxSkillsLimit$ | flAsync"
          [selectedSkills]="selectedSkills$ | flAsync"
          (deselectSkill)="handleDeselectSkill($event)"
        >
        </fl-skills-selected>
      </div>

      <fl-sticky-footer
        [flHideDesktop]="true"
        [position]="StickyFooterPosition.FIXED"
        [stickyStatic]="footerStickyStatic"
        [fullWidth]="footerFullWidth"
      >
        <div class="SaveStickyButton">
          <ng-container
            *ngIf="
              showErrorMessage && (selectedSkills$ | flAsync)?.length === 0;
              else default
            "
            i18n="No selected skills error message"
          >
            <p class="text-error">Please select at least one skill.</p>
          </ng-container>
          <ng-template #default>
            <fl-callout
              i18n-mobileHeaderTitle="Selected Skills callout title"
              mobileHeaderTitle="Selected Skills"
              class="SkillsSelectedContainer"
              [edgeToEdge]="true"
              [mobileCloseButton]="true"
              [mobileFullscreen]="true"
            >
              <fl-callout-trigger>
                <fl-link
                  *ngIf="selectedSkills$ | flAsync as selectedSkills"
                  flTrackingLabel="ViewSelectedSkills"
                >
                  <ng-container
                    *ngIf="selectedSkills.length === 1; else pluralState"
                    i18n="Number of skills selected message in singular form"
                  >
                    {{ selectedSkills.length }} skill selected
                  </ng-container>
                  <ng-template #pluralState>
                    <ng-container
                      i18n="Number of skills selected message in plural form"
                    >
                      {{ selectedSkills.length }} skills selected
                    </ng-container>
                  </ng-template>
                </fl-link>
              </fl-callout-trigger>

              <fl-callout-content>
                <fl-skills-selected
                  class="SkillsSelectedWrapper"
                  [showErrorMessage]="
                    showErrorMessage &&
                    (selectedSkills$ | flAsync)?.length === 0
                  "
                  [matchingSkillCount]="matchingSkillCount$ | flAsync"
                  [maxSkillsLimit]="maxSkillsLimit$ | flAsync"
                  [selectedSkills]="selectedSkills$ | flAsync"
                  (deselectSkill)="handleDeselectSkill($event)"
                >
                </fl-skills-selected>
              </fl-callout-content>
            </fl-callout>
          </ng-template>
          <fl-button
            flTrackingLabel="{{
              buttonType === SkillsButtonType.NEXT
                ? 'ClickNext'
                : 'SaveSkillsButton'
            }}"
            [busy]="
              (busySavingSkills$ | flAsync) === true &&
              (selectedSkills$ | flAsync)?.length !== 0 &&
              !showErrorMessage
            "
            [color]="ButtonColor.SECONDARY"
            [size]="ButtonSize.LARGE"
            (click)="handleSaveSkills()"
          >
            <ng-container
              *ngIf="buttonType === SkillsButtonType.NEXT; else saveButton"
              i18n="Save skills next button"
            >
              Next
            </ng-container>
            <ng-template #saveButton>
              <ng-container i18n="Save skills button"> Save </ng-container>
            </ng-template>
          </fl-button>
        </div>
      </fl-sticky-footer>

      <div
        class="SkillsFooter"
        [flShowDesktop]="true"
        [flMarginBottom]="'large'"
      >
        <fl-button
          flTrackingLabel="{{
            buttonType === SkillsButtonType.NEXT
              ? 'ClickNext'
              : 'SaveSkillsButton'
          }}"
          [busy]="
            (busySavingSkills$ | flAsync) === true &&
            (selectedSkills$ | flAsync)?.length !== 0 &&
            !showErrorMessage
          "
          [color]="ButtonColor.SECONDARY"
          [size]="ButtonSize.LARGE"
          (click)="handleSaveSkills()"
        >
          <ng-container
            *ngIf="buttonType === SkillsButtonType.NEXT; else saveButton"
            i18n="Save skills next button"
          >
            Next
          </ng-container>
          <ng-template #saveButton>
            <ng-container i18n="Save skills button"> Save </ng-container>
          </ng-template>
        </fl-button>
      </div>
    </div>
  `,
  styleUrls: ['./skills.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillsComponent implements OnInit, OnChanges, OnDestroy {
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontColor = FontColor;
  SkillsButtonType = SkillsButtonType;
  StickyFooterPosition = StickyFooterPosition;

  private readonly OTHERS_CATEGORY: Category = {
    name: 'Jobs for Anyone',
    id: 99,
  };
  private readonly DELOITTE_SKILLS_FILTER: readonly number[] = [88, 252]; // Dating and XXX skills

  private _busySavingSkills$ = new BehaviorSubject<boolean>(false);
  private _loadingSkillSearch$ = new Subject<boolean>();
  private _maxSkillsSubject$ = new Subject<number>();
  private _searchText$ = new BehaviorSubject<string>('');
  private _selectedCategory$ = new Subject<Category>();
  private _selectedSkills$ = new BehaviorSubject<readonly Skill[]>([]);
  private _showCategories$ = new BehaviorSubject<boolean>(true);

  busySavingSkills$ = this._busySavingSkills$.asObservable();
  loadingSkillSearch$ = this._loadingSkillSearch$.asObservable();
  maxSkillsSubject$ = this._maxSkillsSubject$.asObservable();
  searchText$ = this._searchText$.asObservable();
  selectedCategory$ = this._selectedCategory$.asObservable();
  selectedSkills$ = this._selectedSkills$.asObservable();
  showCategories$ = this._showCategories$.asObservable();

  userDocument: DatastoreDocument<UsersCollection>;
  userSkillsDocument: DatastoreDocument<UserSkillsCollection>;
  private filteredSkillsSubscription?: Subscription;
  private skillsInitializeSubscription?: Subscription;
  private skillSearchSubscription?: Subscription;

  categories$: Observable<readonly Category[]>;
  filteredSkills$: Observable<readonly Skill[]>;
  includeLocalInSkillsLimit$: Observable<boolean>;
  matchingSkillCount$: Observable<number>;
  maxSkillsLimit$: Observable<number>;
  userId$: Observable<number>;

  changesMade = false;
  showSavingSkillError = false;

  showErrorMessage = false;

  @Input() buttonType: SkillsButtonType = SkillsButtonType.SAVE;
  @Input() maxSkillLimit: number;

  // Pre-selected category id;
  @Input() preSelectedCategoryId?: number;

  // Pre-selected skills ids
  @Input() preSelectedSkillIds?: readonly number[];

  // Needed for the tracking added on logic
  @Input() trackingSection: string;

  @Output() saveSkills = new EventEmitter<boolean>();
  @Output() showMaxSkillsLimit = new EventEmitter();
  @Input() footerStickyStatic = false;
  @Input() footerFullWidth = true;

  constructor(
    private auth: Auth,
    private datastore: Datastore,
    private featureFlagsService: FeatureFlagsService,
    private timeUtils: TimeUtils,
    private tracking: Tracking,
  ) {}

  ngOnInit(): void {
    const allSkillsCollection$ = this.datastore
      .collection<SkillsCollection>('skills', q =>
        q.orderBy('activeProjectCount', OrderByDirection.DESC),
      )
      .valueChanges()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.userSkillsDocument =
      this.datastore.document<UserSkillsCollection>('userSkills');

    this.userId$ = this.auth.getUserId().pipe(map(id => toNumber(id)));
    this.userDocument = this.datastore.document<UsersCollection>(
      'users',
      this.userId$,
    );

    // filtering out xxx and dating skills for deloitte
    // TODO: T131917 - filter these on backend
    const allSkills$ = combineLatest([
      allSkillsCollection$,
      this.userDocument.valueChanges(),
    ]).pipe(
      map(([allSkills, user]) =>
        user.isDeloitteDcUser
          ? allSkills.filter(
              skill => !this.DELOITTE_SKILLS_FILTER.includes(skill.id),
            )
          : allSkills,
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.skillsInitializeSubscription = combineLatest([
      this.userSkillsDocument.valueChanges().pipe(distinctUntilChanged()),
      allSkills$.pipe(distinctUntilChanged()),
      this.busySavingSkills$.pipe(distinctUntilChanged()),
    ]).subscribe(([usersSkills, skills, savingSkills]) => {
      // Don't reinitialise after saving the skills as we close the form after this.
      if (savingSkills) {
        return;
      }

      const selectedSkills: Skill[] = [];

      skills.forEach(skill => {
        const hasSkill =
          usersSkills.skills.includes(skill.id) ||
          this.preSelectedSkillIds?.includes(skill.id);
        if (hasSkill) {
          selectedSkills.push(skill);
        }
      });

      this._selectedSkills$.next(selectedSkills);
    });

    const membershipBenefitsDocument$ =
      this.datastore.document<MembershipBenefitsCollection>(
        'membershipBenefits',
        'skills_limit',
      );

    this.maxSkillsLimit$ = combineLatest([
      membershipBenefitsDocument$.valueChanges(),
      this.maxSkillsSubject$.pipe(startWith(undefined)),
    ]).pipe(map(([benefit, maxSkills]) => maxSkills || benefit.value));

    this.skillSearchSubscription = this.searchText$.subscribe(() => {
      this._loadingSkillSearch$.next(true);
    });

    this.matchingSkillCount$ = this.selectedSkills$.pipe(
      map(skills =>
        skills.reduce((prev, cur) => prev + cur.activeProjectCount, 0),
      ),
    );

    this.filteredSkills$ = combineLatest([
      allSkills$,
      this.selectedCategory$.pipe(startWith(undefined)),
      this.searchText$.pipe(this.timeUtils.rxDebounceTime(500)),
    ]).pipe(
      map(([skills, selectedCategory, searchInputText]) => {
        // If the user has used the skill search input, prioritise over the
        // selected category. Search on both the skill name and category name.
        if (searchInputText) {
          const searchTerm = searchInputText.toLowerCase();
          const filteredSkills = skills.filter(
            skill =>
              (skill.category &&
                skill.category.name &&
                skill.category.name.toLowerCase().includes(searchTerm)) ||
              skill.name.toLowerCase().includes(searchTerm),
          );
          this._loadingSkillSearch$.next(false);
          return filteredSkills;
        }
        // If a category has been selected return all skills with that category.
        if (selectedCategory) {
          const filteredSkills = skills.filter(
            skill =>
              skill.category && skill.category.id === selectedCategory.id,
          );
          this._loadingSkillSearch$.next(false);
          return filteredSkills;
        }

        this._loadingSkillSearch$.next(false);
        // The user has not selected a category or used the search box.
        return [];
      }),
    );

    this.filteredSkillsSubscription = this.filteredSkills$.subscribe(() => {
      this._loadingSkillSearch$.next(true);
    });

    this.categories$ = allSkills$.pipe(
      // Take only unique categories from all active skills.
      map(skills => {
        const uniqueCategories = uniqWith(
          skills.map(skill => skill.category).filter(isDefined),
          (t1, t2) => t1.id === t2.id,
        );
        // Check if there are 'Other' category skills so we know if we need
        // to append the category to the end of the list.
        const hasOthersCategory = !!uniqueCategories.find(
          category => category.id === this.OTHERS_CATEGORY.id,
        );
        return [
          ...uniqueCategories
            .filter(category => category.id !== this.OTHERS_CATEGORY.id)
            .sort((a, b) => a.id - b.id),
          ...(hasOthersCategory ? [this.OTHERS_CATEGORY] : []),
        ];
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    firstValueFrom(this.categories$.pipe(untilDestroyed(this))).then(
      categories => {
        let selectedCategory;
        // preselect the preSelectedCategoryId
        if (this.preSelectedCategoryId) {
          selectedCategory = categories.find(
            category => category.id === this.preSelectedCategoryId,
          );
        } else if (categories.length === 1) {
          // preselect the category when there is only one category
          [selectedCategory] = categories;
        }
        if (selectedCategory) {
          this.handleSelectCategory(selectedCategory);
        }
      },
    );

    this.includeLocalInSkillsLimit$ = this.featureFlagsService.getFlag(
      Feature.INCLUDE_LOCAL_IN_SKILLS_LIMIT,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('maxSkillLimit' in changes) {
      this._maxSkillsSubject$.next(this.maxSkillLimit);
    }
  }

  handleSelectCategory(selectedCategory: Category): void {
    this._selectedCategory$.next(selectedCategory);
    this.showCategories(false);
  }

  handleSelectSkill(selectedSkill: Skill): void {
    this.changesMade = true;

    firstValueFrom(this.selectedSkills$.pipe(untilDestroyed(this))).then(
      skills => {
        this._selectedSkills$.next([...skills, selectedSkill]);
      },
    );

    const eventName = this._searchText$.getValue()
      ? 'SelectSkillFromSearch'
      : 'SelectSkillFromCategory';
    this.tracking.trackCustomEvent(eventName, this.trackingSection, {
      label: eventName,
    });
  }

  handleDeselectSkill(deselectedSkill: Skill): void {
    this.changesMade = true;

    firstValueFrom(this.selectedSkills$.pipe(untilDestroyed(this))).then(
      skills => {
        this._selectedSkills$.next(
          skills.filter(skill => skill !== deselectedSkill),
        );
      },
    );

    const eventName = 'RemoveSelectedSkill';
    this.tracking.trackCustomEvent(eventName, this.trackingSection, {
      label: eventName,
    });
  }

  handleSaveSkills(): void {
    firstValueFrom(
      combineLatest([this.userId$, this.selectedSkills$]).pipe(
        untilDestroyed(this),
      ),
    ).then(([userId, selectedSkills]) => {
      const skillIds = selectedSkills.map(skill => skill.id);
      const userSkill: UserSkill = {
        id: userId,
        skills: skillIds,
      };

      // Only update the skills if the user has at least 1 skill selected.
      if (skillIds.length > 0) {
        this.showErrorMessage = false;
        this.userSkillsDocument
          .update(userSkill)
          .then(response => this.handleSaveResponse(response));
      } else {
        this._busySavingSkills$.next(true);
        this.showErrorMessage = true;
      }
    });
  }

  handleSaveResponse(
    response:
      | BackendDeleteResponse<UserSkillsCollection>
      | BackendUpdateResponse<UserSkillsCollection>,
  ): void {
    this._busySavingSkills$.next(false);

    if (response.status === 'error') {
      this.showSavingSkillError = true;
      return;
    }

    this.saveSkills.emit(this.changesMade);
  }

  handleSearchText(searchText: string): void {
    this._searchText$.next(searchText);

    const eventName = 'SearchSkill';
    this.tracking.trackCustomEvent(eventName, this.trackingSection, {
      label: eventName,
    });

    // in mobile view, hide categories and
    // show the search results upon typing
    if (searchText) {
      this.showCategories(false);
    }
  }

  handleShowMaxSkillsLimit(): void {
    this.showMaxSkillsLimit.emit();
  }

  showCategories(toShow: boolean): void {
    this._showCategories$.next(toShow);
  }

  ngOnDestroy(): void {
    if (this.filteredSkillsSubscription) {
      this.filteredSkillsSubscription.unsubscribe();
    }
    if (this.skillsInitializeSubscription) {
      this.skillsInitializeSubscription.unsubscribe();
    }
    if (this.skillSearchSubscription) {
      this.skillSearchSubscription.unsubscribe();
    }
  }
}
